import React from "react";
import { SITE } from "../config/site";
import { Meta } from "../layout/EducationMeta";
import Link from "../components/Link";
import GlassCard from "../components/ui/GlassCard";
import Container from "../components/ui/Container";
import Eyebrow from "../components/ui/Eyebrow";
import FaqAccordion from "../components/FaqAccordion";
import LifeMovesHero from "../components/home/LifeMovesHero";
import PortfolioFreedomSection from "../components/home/PortfolioFreedomSection";

function AppleMark({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 384 512" aria-hidden="true" className={className} fill="currentColor">
      <path d="M318.7 268.6c-.3-49.2 40.4-72.8 42.2-74-23-33.6-58.7-38.2-71.3-38.7-30.4-3.1-59.3 17.9-74.7 17.9-15.4 0-39.2-17.4-64.5-16.9-33.1 .5-63.6 19.2-80.5 48.8-34.4 59.6-8.8 147.8 24.7 196.2 16.4 23.7 36 50.3 61.7 49.3 24.8-1 34.2-16 64.1-16 29.8 0 38.3 16 64.6 15.5 26.7-.5 43.6-24.2 59.9-48 18.9-27.6 26.7-54.3 27-55.7-.6-.3-51.7-19.9-52-78.7zM265.1 114.1c13.7-16.6 22.9-39.7 20.4-62.8-19.7 .8-43.6 13.1-57.8 29.7-12.7 14.7-23.8 38.3-20.8 60.9 22.1 1.7 44.5-11.2 58.2-27.8z" />
    </svg>
  );
}

function YouTubeEmbed({ videoId, title = "Stratify walkthrough" }) {
  const safeId = String(videoId || "").trim();
  if (!safeId) return null;
  const src = `https://www.youtube.com/embed/${safeId}?playsinline=1&controls=1&modestbranding=1&rel=0`;
  return (
    <div className="relative w-full overflow-hidden rounded-[28px] bg-ink shadow-device ring-1 ring-ink/10">
      <div className="relative w-full pt-[56.25%]">
        <iframe
          className="absolute inset-0 h-full w-full"
          src={src}
          title={title}
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    </div>
  );
}

const faqItems = [
  {
    q: "Do I need to know how to code?",
    a: "No. You build buy and sell conditions in a visual rule builder. Stratify is designed for systematic swing traders who want structure without writing scripts.",
    schemaText:
      "No. You build buy and sell conditions in a visual rule builder. Stratify is designed for systematic swing traders who want structure without writing scripts.",
  },
  {
    q: "Can I connect my brokerage?",
    a: (
      <p>
        Supported brokerage connections can bring holdings into the same view as your saved
        strategies. Availability varies by broker and permissions. Connecting an account does
        not mean Stratify places live orders.{" "}
        <Link to="/integrations" className="font-medium text-hint-deep underline underline-offset-4">
          See all integrations
        </Link>
        .
      </p>
    ),
    schemaText:
      "Supported brokerage connections can bring holdings into the same view as saved strategies. Availability varies by broker and permissions. Connecting an account does not mean Stratify places live orders.",
  },
  {
    q: "Does Stratify trade real money automatically?",
    a: "No. Automated live trading is not included. Cloud paper trading simulates fills so you can follow your rules without placing live orders.",
    schemaText:
      "No. Automated live trading is not included. Cloud paper trading simulates fills so you can follow your rules without placing live orders.",
  },
  {
    q: "How does paper trading differ from a backtest?",
    a: "A backtest is a historical simulation with stated assumptions. Paper trading follows the same saved rules forward in time and records simulated fills, timing, and latency. Neither is a forecast of future performance.",
    schemaText:
      "A backtest is a historical simulation with stated assumptions. Paper trading follows the same saved rules forward in time and records simulated fills, timing, and latency. Neither is a forecast of future performance.",
  },
  {
    q: "Which devices are supported?",
    a: "Stratify is an iOS app for iPhone. The website is for product information, support, and account billing — not a trading dashboard.",
    schemaText:
      "Stratify is an iOS app for iPhone. The website is for product information, support, and account billing — not a trading dashboard.",
  },
  {
    q: "Is this financial advice?",
    a: "No. Stratify is a research and simulation tool. Historical results do not predict future performance. Not financial advice. Educational purposes only.",
    schemaText:
      "No. Stratify is a research and simulation tool. Historical results do not predict future performance. Not financial advice. Educational purposes only.",
  },
  {
    q: "How do I get help?",
    a: (
      <p>
        Email{" "}
        <a className="font-medium text-hint-deep underline underline-offset-4" href={`mailto:${SITE.supportEmail}`}>
          {SITE.supportEmail}
        </a>
        . Do not email passwords or card numbers. Visit{" "}
        <Link to="/support" className="font-medium text-hint-deep underline underline-offset-4">
          Support
        </Link>
        .
      </p>
    ),
    schemaText: `Email ${SITE.supportEmail}. Do not email passwords or card numbers.`,
  },
];

export default function HomePage() {
  const explainerId = process.env.REACT_APP_HERO_YT_ID || "xhYQAockM-o";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE.name,
      url: SITE.url,
      description: SITE.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      name: SITE.appName,
      operatingSystem: "iOS",
      applicationCategory: "FinanceApplication",
      url: SITE.url,
      downloadUrl: SITE.appStoreUrl,
      description: SITE.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: it.schemaText,
        },
      })),
    },
  ];

  return (
    <div className="w-full">
      <Meta
        title="Stratify — No-Code Backtesting & Paper Automation"
        description={SITE.description}
        canonical={SITE.url}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <LifeMovesHero />
      <PortfolioFreedomSection />

      <section id="how-it-works" className="scroll-mt-28 py-20 md:py-28">
        <Container className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Eyebrow>The Stratify workflow</Eyebrow>
            <h2 className="mt-4 text-4xl font-semibold tracking-display text-ink md:text-5xl">
              A method you can make your own.
            </h2>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted">
              Historical results do not predict future performance.
            </p>
          </div>
          <div className="grid gap-6 md:col-span-7">
            {[
              {
                n: "01",
                title: "Build",
                href: "/building-strategies",
                body: "Define the conditions behind your buy and sell decisions.",
              },
              {
                n: "02",
                title: "Test",
                href: "/building-strategies",
                body: "Review historical trades, drawdowns, and assumptions.",
              },
              {
                n: "03",
                title: "Follow",
                href: "/bot-trader",
                body: "Observe paper fills and keep your strategy in view.",
              },
            ].map((step) => (
              <Link key={step.n} to={step.href} className="group block">
                <div className="flex items-start justify-between gap-4 border-t border-ink/8 pt-5">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">{step.n} {step.title}</div>
                    <p className="mt-2 text-sm text-ink/80">{step.body}</p>
                  </div>
                  <span className="text-hint-deep opacity-0 transition group-hover:opacity-100">↗</span>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-bg1 py-20 md:py-28">
        <Container className="grid items-center gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Eyebrow index="01">Portfolio and strategies</Eyebrow>
            <h2 className="mt-4 text-4xl font-semibold tracking-display text-ink md:text-5xl">
              Everything you own. The rules you choose.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted">
              Bring supported brokerage holdings and your saved strategies into the same view.
            </p>
            <Link to="/building-strategies#saved-strategies" className="mt-6 inline-flex text-sm font-medium text-hint-deep hover:underline">
              Explore portfolio connections ↗
            </Link>
          </div>
          <div className="md:col-span-7">
            <GlassCard className="overflow-hidden p-3">
              <img
                src="/images/watchlistportimage.png"
                alt="Holdings and saved strategies in Stratify"
                className="h-auto w-full rounded-2xl object-cover"
              />
            </GlassCard>
            <p className="mt-3 text-right text-[11px] text-muted">
              Product concept · illustrative data. Broker support and permissions vary.
            </p>
          </div>
        </Container>
      </section>

      <section className="py-20 md:py-28">
        <Container>
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-6">
              <Eyebrow index="02">Research with context</Eyebrow>
              <h2 className="mt-4 text-4xl font-semibold tracking-display text-ink md:text-5xl">
                Look past the headline return.
              </h2>
            </div>
            <div className="md:col-span-6 md:pt-10">
              <p className="text-base leading-relaxed text-muted">
                A backtest is a starting point. Examine the trades, the drawdowns, and the assumptions behind the result.
              </p>
              <Link to="/building-strategies" className="mt-5 inline-flex text-sm font-medium text-hint-deep hover:underline">
                Explore backtesting ↗
              </Link>
            </div>
          </div>
          <GlassCard className="mt-10 overflow-hidden p-4 md:p-6">
            <img
              src="/images/resultsportimage.png"
              alt="Backtest overview with return, drawdown, and trade context"
              className="h-auto w-full rounded-2xl object-cover"
            />
            <p className="mt-4 text-xs text-muted">Historical simulation. Not a forecast.</p>
          </GlassCard>
        </Container>
      </section>

      <section className="bg-bg1 py-20 md:py-28">
        <Container className="grid items-center gap-12 md:grid-cols-12">
          <div className="md:col-span-6">
            <GlassCard className="overflow-hidden p-4">
              <img
                src="/images/screenshot2.png"
                alt="Strategy scoring and discovery filters"
                className="h-auto w-full rounded-2xl object-cover"
              />
            </GlassCard>
          </div>
          <div className="md:col-span-6">
            <Eyebrow index="03">Your evaluation criteria</Eyebrow>
            <h2 className="mt-4 text-4xl font-semibold tracking-display text-ink md:text-5xl">
              Research, on your terms.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted">
              Explore rule combinations, separate your data samples, and compare strategies using your priorities. A provisional score is a comparison tool, not a prediction.
            </p>
            <Link to="/strategy-discovery" className="mt-6 inline-flex text-sm font-medium text-hint-deep hover:underline">
              Explore strategy discovery ↗
            </Link>
          </div>
        </Container>
      </section>

      <section className="py-20 md:py-28">
        <Container className="grid items-center gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Eyebrow index="04">Paper execution</Eyebrow>
            <h2 className="mt-4 text-4xl font-semibold tracking-display text-ink md:text-5xl">
              A signal is only part of the story.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted">
              Follow your strategy through paper execution. Review the signal, simulated fill, and recorded timing.
            </p>
            <Link to="/bot-trader" className="mt-6 inline-flex text-sm font-medium text-hint-deep hover:underline">
              Explore paper trading ↗
            </Link>
          </div>
          <div className="md:col-span-7">
            <GlassCard className="p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-ink">AAPL / EMA crossover</div>
                <span className="rounded-full bg-hint/15 px-2 py-1 text-[10px] font-semibold uppercase text-hint-deep">
                  Paper
                </span>
              </div>
              <ol className="mt-6 space-y-5">
                {[
                  ["Signal recorded", "Buy condition met on the saved rule set"],
                  ["Paper fill", "Simulated fill recorded for review"],
                  ["Signal-to-fill time", "Latency is measured and shown, not hidden"],
                ].map(([title, body]) => (
                  <li key={title} className="flex gap-3">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-hint-strong" />
                    <div>
                      <div className="text-sm font-semibold text-ink">{title}</div>
                      <div className="text-sm text-muted">{body}</div>
                    </div>
                  </li>
                ))}
              </ol>
              <Link to="/bot-trader#notifications" className="mt-6 inline-flex text-sm font-medium text-hint-deep hover:underline">
                Review activity and alerts ↗
              </Link>
            </GlassCard>
          </div>
        </Container>
      </section>

      <section className="bg-bg1 py-20 md:py-28">
        <Container className="grid items-center gap-12 md:grid-cols-12">
          <div className="md:col-span-7">
            <YouTubeEmbed videoId={explainerId} />
          </div>
          <div className="md:col-span-5">
            <Eyebrow>The product, in practice</Eyebrow>
            <h2 className="mt-4 text-4xl font-semibold tracking-display text-ink md:text-5xl">
              See the process come together.
            </h2>
            <p className="mt-5 text-sm text-muted">Product concept preview. Watch the walkthrough.</p>
          </div>
        </Container>
      </section>

      <section className="py-20 md:py-28">
        <Container>
          <div className="grid gap-10 md:grid-cols-12 md:items-end">
            <div className="md:col-span-5">
              <Eyebrow>Pricing</Eyebrow>
              <h2 className="mt-4 text-4xl font-semibold tracking-display text-ink md:text-5xl">
                Room to grow.
              </h2>
              <p className="mt-4 text-muted">Choose the plan that fits your workflow.</p>
              <Link
                to="/pricing"
                className="mt-6 inline-flex items-center rounded-full bg-ink px-5 py-3 text-sm font-semibold text-white"
              >
                Compare plans ↗
              </Link>
            </div>
            <div className="grid gap-6 md:col-span-7 md:grid-cols-3">
              {[
                ["Free", "Start with the essentials."],
                ["Pro", "More room for research."],
                ["Elite", "Expand your paper automation."],
              ].map(([name, body]) => (
                <div key={name}>
                  <div className="text-xl font-semibold text-ink">{name}</div>
                  <p className="mt-2 text-sm text-muted">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <section className="border-t border-ink/8 py-16">
        <Container className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-muted">{SITE.legalName}</p>
          <div className="flex gap-5 text-sm">
            <Link to="/about" className="text-hint-deep hover:underline">
              Meet the team ↗
            </Link>
            <Link to="/support" className="text-hint-deep hover:underline">
              Support ↗
            </Link>
          </div>
        </Container>
      </section>

      <section id="faq" className="scroll-mt-28 bg-bg1 py-20 md:py-28">
        <Container className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Eyebrow>Frequently asked questions</Eyebrow>
            <h2 className="mt-4 text-4xl font-semibold tracking-display text-ink md:text-5xl">
              Before you begin.
            </h2>
          </div>
          <div className="md:col-span-7">
            <FaqAccordion items={faqItems} defaultOpenIndex={0} />
          </div>
        </Container>
      </section>

      <section className="py-20 md:py-28">
        <Container className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <div>
            <h2 className="max-w-xl text-4xl font-semibold tracking-display text-ink md:text-6xl">
              Make your next move a considered one.
            </h2>
            <p className="mt-4 text-sm text-muted">Build. Backtest. Paper trade.</p>
          </div>
          <a
            href={SITE.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-white"
          >
            <AppleMark />
            Get Stratify for iPhone
          </a>
        </Container>
      </section>
    </div>
  );
}
